import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { MessageStatusChip } from "@/components/StatusChip";
import { toast } from "sonner";
import { History, FileDown } from "lucide-react";

const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("ar-SA", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const statusAr = (s) => ({ sent: "تم الإرسال", failed: "فشل", sending: "قيد الإرسال", pending: "قيد الإرسال" }[s] || s);

export default function Logs() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/messages").then((r) => setLogs(r.data)).catch(() => {}); }, []);

  const exportCSV = () => {
    if (!logs.length) { toast.error("لا توجد رسائل للتصدير"); return; }
    const header = ["التاريخ", "الطالب", "النوع", "الحالة", "رقم الجوال", "النص"];
    const esc = (x) => `"${String(x ?? "").replace(/"/g, '""')}"`;
    const rows = logs.map((m) => [fmt(m.created_at), m.student_name, m.message_type, statusAr(m.status), m.parent_phone, m.body].map(esc).join(","));
    const csv = "\uFEFF" + [header.map(esc).join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `message_log_${(user?.school_name || "list").replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!logs.length) { toast.error("لا توجد رسائل للتصدير"); return; }
    const today = new Date().toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { year: "numeric", month: "long", day: "numeric" });
    const rows = logs.map((m, i) =>
      `<tr><td>${i + 1}</td><td>${fmt(m.created_at)}</td><td>${m.student_name || ""}</td><td>${m.message_type || ""}</td><td class="st ${m.status}">${statusAr(m.status)}</td></tr>`).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>سجل الرسائل</title>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap" rel="stylesheet">
      <style>*{font-family:'Cairo',sans-serif;box-sizing:border-box;}body{padding:24px;color:#0f172a;}
      .head{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #065f46;padding-bottom:12px;margin-bottom:12px;}
      .head h1{color:#065f46;margin:0;font-size:20px;}.head .meta{color:#64748b;font-size:12px;text-align:left;}
      table{width:100%;border-collapse:collapse;}th,td{border:1px solid #e2e8f0;padding:7px 9px;text-align:right;font-size:12px;}
      th{background:#f1f5f9;color:#334155;}.st.sent{color:#059669;font-weight:700;}.st.failed{color:#dc2626;font-weight:700;}.st.sending,.st.pending{color:#d97706;font-weight:700;}
      @media print{.noprint{display:none;}}</style></head><body>
      <div class="head"><h1>سجل الرسائل — ${user?.school_name || ""}</h1>
      <div class="meta">المرشد: ${user?.name || ""}<br>${today}<br>الإجمالي: ${logs.length} رسالة</div></div>
      <table><thead><tr><th>#</th><th>التاريخ</th><th>الطالب</th><th>النوع</th><th>الحالة</th></tr></thead><tbody>${rows}</tbody></table>
      <button class="noprint" onclick="window.print()" style="margin-top:16px;padding:10px 20px;background:#065f46;color:#fff;border:none;border-radius:8px;cursor:pointer;font-family:'Cairo';">طباعة / حفظ PDF</button>
      <script>setTimeout(function(){window.print();},700);</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("يرجى السماح بالنوافذ المنبثقة لتصدير PDF"); return; }
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">سجل الرسائل</h1>
          <p className="text-slate-400">جميع الرسائل المرسلة لأولياء الأمور وحالاتها</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPDF} className="gap-2" data-testid="log-export-pdf">
            <FileDown className="h-4 w-4" /> تصدير PDF
          </Button>
          <Button variant="outline" onClick={exportCSV} className="gap-2" data-testid="log-export-csv">
            <FileDown className="h-4 w-4" /> تصدير Excel
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
              <History className="h-10 w-10" />
              <p>لا توجد رسائل في السجل بعد</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">التاريخ</TableHead>
                  <TableHead className="text-right">الطالب</TableHead>
                  <TableHead className="hidden text-right md:table-cell">الجوال</TableHead>
                  <TableHead className="text-right">النوع</TableHead>
                  <TableHead className="hidden text-right md:table-cell">النص</TableHead>
                  <TableHead className="text-center">الحالة</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((m) => (
                  <TableRow key={m.id} data-testid={`log-row-${m.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmt(m.created_at)}</TableCell>
                    <TableCell className="font-semibold text-slate-800">{m.student_name}</TableCell>
                    <TableCell dir="ltr" className="hidden text-right font-mono text-xs md:table-cell">{m.parent_phone}</TableCell>
                    <TableCell><span className="text-xs">{m.message_type}</span></TableCell>
                    <TableCell className="hidden max-w-xs truncate text-xs text-slate-500 md:table-cell" title={m.body}>{m.body}</TableCell>
                    <TableCell className="text-center">
                      <MessageStatusChip status={m.status} />
                      {m.error && <p className="mt-1 text-[10px] text-red-500">{m.error}</p>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
